import type { EchoflicksMediaRoom, EchoflicksSessionMedia } from "../types";
import type { SceneI2VAudit } from "./scriptStory";

// ---------------------------------------------------------------------------
// syncMedia — pure resolver mapping each script scene to the ORIGINAL uploaded
// media it was cut from (Director View, QCSV-002).
//
// Resolution order per scene:
//   1. `source_storage_key` — exact key match against media rows (new scripts;
//      immune to basename collisions).
//   2. `video_prompt.file` basename — today's Storyboard behaviour (all echoes).
//   3. i2v `reference_frames.first_frame.storageKey` — animated scenes.
//   4. TitleCard → typed placeholder carrying the on-screen title text.
//
// Native dimensions, priority: script `source_width/height` → media-row labels
// (`media.analyzed` enrichment, when populated) → naturalWidth/Height on load
// (the component's job — this module returns null and the panel measures).
// ---------------------------------------------------------------------------

export type SceneMediaOrientation = "portrait" | "landscape" | "square";

export type SceneMediaMatch = "storage_key" | "basename" | "i2v_reference";

export type SceneSourceMedia =
  | {
      kind: "image";
      media: EchoflicksSessionMedia;
      storageKey: string;
      matchedBy: SceneMediaMatch;
      sourceFile: string | null;
      sourceMediaId: string | null;
      width: number | null;
      height: number | null;
      orientation: SceneMediaOrientation | null;
      framingIntent: string | null;
      /** Room the director's room map assigned the photo to (FPR-022), when known. */
      room: EchoflicksMediaRoom | null;
    }
  | {
      kind: "video";
      media: EchoflicksSessionMedia;
      storageKey: string;
      matchedBy: SceneMediaMatch;
      sourceFile: string | null;
      sourceMediaId: string | null;
      width: number | null;
      height: number | null;
      orientation: SceneMediaOrientation | null;
      framingIntent: string | null;
      /** Room the director's room map assigned the photo to (FPR-022), when known. */
      room: EchoflicksMediaRoom | null;
      /** VideoScene trim window from `video_prompt.cuts` (source-video seconds). */
      trimStartSeconds: number | null;
      trimEndSeconds: number | null;
    }
  | {
      kind: "title";
      titleText: string | null;
      cardType: string | null;
    }
  | {
      kind: "missing";
      sourceFile: string | null;
    };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function num(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

export function orientationOf(
  width: number | null,
  height: number | null,
): SceneMediaOrientation | null {
  if (width == null || height == null || width <= 0 || height <= 0) return null;
  if (width === height) return "square";
  return width > height ? "landscape" : "portrait";
}

function basenameOf(key: string | null): string | null {
  if (!key) return null;
  const name = key.split("/").at(-1) ?? null;
  return name && name.trim() ? name : null;
}

/**
 * Feature-detect the per-photo room the indexer copied from `media.labeled`
 * onto the MEDIA row (FPR-022). Absent on pre-feature echoes; a room without
 * a name is treated as absent.
 */
export function mediaRoom(
  media: EchoflicksSessionMedia,
): EchoflicksMediaRoom | null {
  const room: unknown = media.room;
  if (!isRecord(room)) return null;
  const name = str(room.name);
  if (!name) return null;
  return {
    room_id: str(room.room_id),
    name,
    category: str(room.category),
    confidence: num(room.confidence),
    // floor_plan.v2 (FPV-031): the level the room sits on, when the room map
    // resolved one. Absent on every v1 echo.
    level_id: str(room.level_id),
    level_label: str(room.level_label),
  };
}

function mediaStorageKey(item: EchoflicksSessionMedia): string | null {
  return str(item.storage_path) ?? str(item.s3_key);
}

/** First-match-wins basename map — mirrors `buildMediaMap` in scriptStory. */
function mediaByBasename(
  media: EchoflicksSessionMedia[],
): Map<string, EchoflicksSessionMedia> {
  const byFilename = new Map<string, EchoflicksSessionMedia>();
  for (const item of media) {
    const filename =
      basenameOf(mediaStorageKey(item)) ?? basenameOf(str(item.local_path));
    if (filename && !byFilename.has(filename)) {
      byFilename.set(filename, item);
    }
  }
  return byFilename;
}

function mediaByStorageKey(
  media: EchoflicksSessionMedia[],
): Map<string, EchoflicksSessionMedia> {
  const byKey = new Map<string, EchoflicksSessionMedia>();
  for (const item of media) {
    const key = mediaStorageKey(item);
    if (key && !byKey.has(key)) {
      byKey.set(key, item);
    }
  }
  return byKey;
}

type SceneMediaScene = {
  index: number;
  sceneId: number | string | null;
  sceneType: string | null;
  sourceFile: string | null;
  sourceStorageKey: string | null;
  sourceMediaId: string | null;
  sourceWidth: number | null;
  sourceHeight: number | null;
  titleText: string | null;
  cardType: string | null;
  cuts: [number, number] | null;
  framingIntent: string | null;
};

function parseScenes(envelope: unknown): SceneMediaScene[] {
  if (!isRecord(envelope)) return [];
  const script = isRecord(envelope.script) ? envelope.script : envelope;
  const videoContent = Array.isArray(script.video_content)
    ? script.video_content
    : [];
  return videoContent.map((raw, index) => {
    const scene = isRecord(raw) ? raw : {};
    const prompt = isRecord(scene.video_prompt) ? scene.video_prompt : {};
    let cuts: [number, number] | null = null;
    if (Array.isArray(prompt.cuts)) {
      const start = num(prompt.cuts[0]);
      const end = num(prompt.cuts[1]);
      if (start != null && end != null && end >= start) cuts = [start, end];
    }
    const sceneId = scene.id;
    return {
      index,
      sceneId:
        typeof sceneId === "number" || typeof sceneId === "string"
          ? sceneId
          : null,
      sceneType: str(scene.type),
      sourceFile: str(prompt.file),
      sourceStorageKey: str(scene.source_storage_key),
      sourceMediaId: str(scene.source_media_id),
      sourceWidth: num(scene.source_width),
      sourceHeight: num(scene.source_height),
      titleText: str(prompt.text) ?? str(prompt.title_card_text),
      cardType: str(prompt.card_type),
      cuts,
      framingIntent: str(scene.framing_intent),
    };
  });
}

function dimensionsFor(
  scene: SceneMediaScene,
  media: EchoflicksSessionMedia,
): { width: number | null; height: number | null } {
  // Script dims first (exact for the pipeline input), media-row labels second.
  const width = scene.sourceWidth ?? num(media.width);
  const height = scene.sourceHeight ?? num(media.height);
  return { width, height };
}

function isVideoMedia(media: EchoflicksSessionMedia): boolean {
  const mime = str(media.mime_type);
  if (mime) return mime.startsWith("video/");
  const key = mediaStorageKey(media);
  return key != null && /\.(mp4|mov|webm|m4v)$/i.test(key);
}

function buildResolved(
  scene: SceneMediaScene,
  media: EchoflicksSessionMedia,
  matchedBy: SceneMediaMatch,
): SceneSourceMedia {
  const storageKey = mediaStorageKey(media);
  if (!storageKey) return { kind: "missing", sourceFile: scene.sourceFile };
  const { width, height } = dimensionsFor(scene, media);
  const base = {
    media,
    storageKey,
    matchedBy,
    sourceFile: scene.sourceFile,
    sourceMediaId: scene.sourceMediaId,
    width,
    height,
    orientation: orientationOf(width, height),
    framingIntent: scene.framingIntent,
    room: mediaRoom(media),
  };
  if (scene.sceneType === "VideoScene" || isVideoMedia(media)) {
    return {
      kind: "video",
      ...base,
      trimStartSeconds: scene.cuts?.[0] ?? null,
      trimEndSeconds: scene.cuts?.[1] ?? null,
    };
  }
  return { kind: "image", ...base };
}

/**
 * Resolve every scene's original media. Returns a map keyed by scene INDEX
 * (positions match `SyncScene.index` from syncTimeline).
 */
export function resolveSceneSources(args: {
  envelope: unknown;
  media: EchoflicksSessionMedia[];
  /** Merged per-scene I2V audits (manifest + journey) for reference frames. */
  sceneI2VByScene?: Map<string | number, SceneI2VAudit> | null;
}): Map<number, SceneSourceMedia> {
  const scenes = parseScenes(args.envelope);
  const uploaded = args.media.filter((item) => item.kind === "uploaded_media");
  const byKey = mediaByStorageKey(uploaded);
  const byName = mediaByBasename(uploaded);
  const resolved = new Map<number, SceneSourceMedia>();

  for (const scene of scenes) {
    if (scene.sceneType === "TitleCardScene") {
      resolved.set(scene.index, {
        kind: "title",
        titleText: scene.titleText,
        cardType: scene.cardType,
      });
      continue;
    }

    // 1. Exact storage-key match — immune to basename collisions.
    if (scene.sourceStorageKey) {
      const media = byKey.get(scene.sourceStorageKey);
      if (media) {
        resolved.set(scene.index, buildResolved(scene, media, "storage_key"));
        continue;
      }
    }

    // 2. Basename match (today's Storyboard behaviour).
    if (scene.sourceFile) {
      const media = byName.get(scene.sourceFile);
      if (media) {
        resolved.set(scene.index, buildResolved(scene, media, "basename"));
        continue;
      }
    }

    // 3. i2v reference frame (animated scenes record the exact original key).
    const audit =
      (scene.sceneId != null
        ? (args.sceneI2VByScene?.get(scene.sceneId) ??
          args.sceneI2VByScene?.get(String(scene.sceneId)))
        : undefined) ?? args.sceneI2VByScene?.get(`index:${scene.index}`);
    const referenceKey = str(audit?.referenceFrames?.firstFrame?.storageKey);
    if (referenceKey) {
      const media = byKey.get(referenceKey);
      if (media) {
        resolved.set(scene.index, buildResolved(scene, media, "i2v_reference"));
        continue;
      }
    }

    resolved.set(scene.index, {
      kind: "missing",
      sourceFile: scene.sourceFile,
    });
  }

  return resolved;
}

/**
 * Scene index → room name for the scenes whose original photo carries a room
 * (FPR-033). Feeds the strip tooltip; the per-scene badge lives in SourcePanel.
 */
export function buildSceneRoomIndex(
  sources: Map<number, SceneSourceMedia>,
): Map<number, string> {
  const index = new Map<number, string>();
  for (const [sceneIndex, source] of sources) {
    if (source.kind !== "image" && source.kind !== "video") continue;
    const name = source.room?.name;
    if (name) index.set(sceneIndex, name);
  }
  return index;
}
